import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PhotographIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ColorSwatchIcon,
  FilmIcon,
  BadgeCheckIcon,
  CollectionIcon,
  SparklesIcon,
  StarIcon,
  ChatAlt2Icon,
  PhoneIcon,
  UserGroupIcon,
  InformationCircleIcon,
  SearchCircleIcon,
  RefreshIcon,
  LocationMarkerIcon,
} from "@heroicons/react/outline";
import {
  getBranchDisplayThunk,
  getDisplayOptionsThunk,
  clearBranchDisplayError,
  resetBranchDisplay,
  setActiveSection,
} from "../../../store/thunks/branchDisplayThunks";
import { clearSuccessMessage } from "../../../store/slices/branchDisplaySlice";

// Section Components
import BrandingSection from "./sections/BrandingSection";
import ImagesSection from "./sections/ImagesSection";
import VideosSection from "./sections/VideosSection";
import HighlightsSection from "./sections/HighlightsSection";
import CollectionsSection from "./sections/CollectionsSection";
import ServicesSection from "./sections/ServicesSection";
import CertificationsSection from "./sections/CertificationsSection";
import AwardsSection from "./sections/AwardsSection";
import TestimonialsSection from "./sections/TestimonialsSection";
import ContactInfoSection from "./sections/ContactInfoSection";
import TeamSection from "./sections/TeamSection";
import StoreInfoSection from "./sections/StoreInfoSection";
import LocationSection from "./sections/LocationSection";
import SEOSection from "./sections/SEOSection";
import BranchDisplayCopyModal from "../modals/BranchDisplayCopyModal";

const SECTIONS = [
  { key: "branding", label: "Branding", icon: ColorSwatchIcon },
  { key: "images", label: "Images", icon: PhotographIcon },
  { key: "videos", label: "Videos", icon: FilmIcon },
  { key: "contact", label: "Contact Info", icon: PhoneIcon },
  { key: "location", label: "Location", icon: LocationMarkerIcon },
  { key: "testimonials", label: "Testimonials", icon: ChatAlt2Icon },
  { key: "highlights", label: "Highlights", icon: BadgeCheckIcon },
  // { key: "collections", label: "Collections", icon: CollectionIcon },
  { key: "services", label: "Services", icon: SparklesIcon },
  { key: "certifications", label: "Certifications", icon: BadgeCheckIcon },
  // { key: "awards", label: "Awards", icon: StarIcon },
  // { key: "team", label: "Team", icon: UserGroupIcon },
  { key: "storeInfo", label: "Store Info", icon: InformationCircleIcon },
  // { key: "seo", label: "SEO", icon: SearchCircleIcon },
];

const BranchDisplayManagement = ({ branch }) => {
  const dispatch = useDispatch();
  const {
    display,
    contactInfo,
    storeInfo,
    seo,
    loading,
    loadingSection,
    error,
    sectionError,
    successMessage,
    activeSection,
    displayOptions,
  } = useSelector((state) => state.branchDisplay);

  const { user: currentAdmin } = useSelector((state) => state.auth);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const isRootAdmin = currentAdmin?.role === "root";

  useEffect(() => {
    if (branch?.code) {
      dispatch(getBranchDisplayThunk(branch.code));
    }
    if (!displayOptions) {
      dispatch(getDisplayOptionsThunk());
    }

    return () => {
      dispatch(resetBranchDisplay());
    };
  }, [dispatch, branch?.code]);

  const handleTabChange = (sectionKey) => {
    dispatch(setActiveSection(sectionKey));
  };

  const handleCloseMessages = () => {
    dispatch(clearSuccessMessage());
    dispatch(clearBranchDisplayError());
  };

  const renderSection = () => {
    const branchCode = branch?.code;

    switch (activeSection) {
      case "branding":
        return (
          <BrandingSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "images":
        return (
          <ImagesSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "videos":
        return (
          <VideosSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "highlights":
        return (
          <HighlightsSection
            branchCode={branchCode}
            isRootAdmin={isRootAdmin}
          />
        );
      case "collections":
        return (
          <CollectionsSection
            branchCode={branchCode}
            isRootAdmin={isRootAdmin}
          />
        );
      case "services":
        return (
          <ServicesSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "certifications":
        return (
          <CertificationsSection
            branchCode={branchCode}
            isRootAdmin={isRootAdmin}
          />
        );
      case "awards":
        return (
          <AwardsSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "testimonials":
        return (
          <TestimonialsSection
            branchCode={branchCode}
            isRootAdmin={isRootAdmin}
          />
        );
      case "contact":
        return (
          <ContactInfoSection
            branchCode={branchCode}
            isRootAdmin={isRootAdmin}
          />
        );
      case "team":
        return (
          <TeamSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "storeInfo":
        return (
          <StoreInfoSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "location":
        return (
          <LocationSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
      case "seo":
        return <SEOSection branchCode={branchCode} isRootAdmin={isRootAdmin} />;
      default:
        return (
          <BrandingSection branchCode={branchCode} isRootAdmin={isRootAdmin} />
        );
    }
  };

  if (loading && !display) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Copy Modal */}
      {showCopyModal && (
        <BranchDisplayCopyModal
          isOpen={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          sourceBranch={branch}
        />
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center">
          <EyeIcon className="h-5 w-5 mr-2" />
          Branch Display Configuration
        </h3>
        <div className="flex gap-2">
          {isRootAdmin && (
            <>
              <button
                onClick={() => setShowCopyModal(true)}
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                Copy to Other Branches
              </button>
              <button
                onClick={() => dispatch(getBranchDisplayThunk(branch.code))}
                className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <RefreshIcon className="h-4 w-4" />
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800 text-sm">{successMessage}</span>
            </div>
            <button
              onClick={handleCloseMessages}
              className="text-green-600 hover:text-green-800"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {(error || sectionError) && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800 text-sm">
                {error || sectionError}
              </span>
            </div>
            <button
              onClick={handleCloseMessages}
              className="text-red-600 hover:text-red-800"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div className="border-b">
        <nav className="flex -mb-px overflow-x-auto">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.key}
                onClick={() => handleTabChange(section.key)}
                className={`px-4 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap text-sm ${
                  activeSection === section.key
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                <Icon className="h-4 w-4" />
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Section Content */}
      <div className="min-h-[400px]">
        {loadingSection ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          renderSection()
        )}
      </div>
    </div>
  );
};

export default BranchDisplayManagement;
